import { SCHEMA } from '@vedalib/editor/lib/brand'
import { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { FieldGroup, FormField } from 'components/formKit'
import { Typography } from 'components/uiKit/Typography'
import { BrandButtonFont, BrandFormFontBase, BrandGoBack } from 'services/Branding/components'
import { FONT_FIELDS } from 'services/Branding/fieldConfigs'
import { genSchema } from 'services/Branding/genSchema'
import { useElementFontCss, useElementFontValues, useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { LinkBrandForms, LinkFormBrandType } from './linkTypes'

export const FONT_FORM_SCHEMA = genSchema(SCHEMA.link.font, {
  link: { ...FONT_FIELDS.base, ...FONT_FIELDS.link },
  h3: FONT_FIELDS.h3,
  base: FONT_FIELDS.base,
})

const TypographyLink: LinkFormBrandType = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <Padding gap sides>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('elements.link.tags.link')}</Typography>
      </BrandGoBack>
      <FormField config={fontSchema.link.fontFamily} />
      <FieldGroup config={fontSchema.link.fontStyleGroup}>
        <FormField config={fontSchema.link.fontWeight} />
        <FormField config={fontSchema.link.fontStyle} />
      </FieldGroup>
      <FormField config={fontSchema.link.fontSize} />
      <FormField config={fontSchema.link.lineHeight} />
      <FormField config={fontSchema.link.color} />
      <FormField config={fontSchema.link.textDecoration} />
      <FormField config={fontSchema.link.hoverColor} />
    </Padding>
  )
}

const TypographyH3: LinkFormBrandType = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.h3}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('elements.link.tags.header')}</Typography>
      </BrandGoBack>
    </BrandFormFontBase>
  )
}

const TypographyBase: LinkFormBrandType = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.base}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('elements.link.tags.description')}</Typography>
      </BrandGoBack>
    </BrandFormFontBase>
  )
}

const LinkTypography: LinkFormBrandType = ({ device, edited, element, setForm }) => {
  const values = useElementFontValues('link', device, element?.font)
  const styles = useElementFontCss('link', device, element?.font)

  const goBack = useCallback(() => setForm?.(BrandFormEnum.STYLE), [setForm])

  return (
    <Padding bottom gap sides>
      <BrandGoBack onClick={goBack}>
        <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
      </BrandGoBack>
      <BrandButtonFont
        edited={edited.font?.link}
        font='link'
        onClick={setForm}
        styles={styles.link}
        values={values.link}
      >
        {t('elements.link.tags.link')}
      </BrandButtonFont>
      <BrandButtonFont
        edited={edited.font?.h3}
        font='h3'
        onClick={setForm}
        styles={styles.h3}
        values={values.h3}
      >
        {t('elements.link.tags.header')}
      </BrandButtonFont>
      <BrandButtonFont
        edited={edited.font?.base}
        font='base'
        onClick={setForm}
        styles={styles.base}
        values={values.base}
      >
        {t('elements.link.tags.description')}
      </BrandButtonFont>
    </Padding>
  )
}

const FORM_MAP: LinkBrandForms = {
  [BrandFormEnum.TYPOGRAPHY]: LinkTypography,
  base: TypographyBase,
  h3: TypographyH3,
  link: TypographyLink,
}

export default FORM_MAP
